import * as React from 'react';
import ReactDOM from 'react-dom';
import { FaTwitter, FaFacebook, FaLinkedin, FaWhatsapp, FaLink, FaShareAlt } from 'react-icons/fa';

interface ShareButtonsProps {
  shares: number;
  onShare: (platform: string, shareText?: string) => void;
  copySuccess: boolean;
  notification: string | null;
  productName?: string;
  score?: number;
  asin?: string;
  productImage?: string;
}

export const ShareButtons: React.FC<ShareButtonsProps> = ({ 
  shares, 
  onShare, 
  copySuccess, 
  notification,
  productName = '',
  score = 0,
  asin = '',
  productImage = ''
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isOpen && 
          dropdownRef.current && 
          !dropdownRef.current.contains(event.target as Node) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#4b5563',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 15px',
    fontSize: '14px',
    fontWeight: 'bold' as const,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    gap: '6px'
  };

  const dropdownButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    backgroundColor: '#4285F4',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    fontWeight: 600 as const,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    width: '100%',
    gap: '8px'
  };

  const getShareUrl = () => {
    return `${window.location.origin}/results/${asin}`;
  };

  const shareUrl = getShareUrl();
  const shareText = productName 
    ? `🚨 AI ROAST ALERT: This listing just got absolutely DESTROYED with a ${score}/100! Even the algorithm is laughing! 🔥 #ListingHomicide` 
    : `🚨 SAVAGE: AI just committed a murder on Amazon! This roast is so brutal it needs a warning label! 🔥 #ListingHomicide`;
  
  const handleShare = (platform: string) => {
    switch (platform) {
      case 'twitter':
        onShare(platform, `🔥 AI Roast Alert: ${score}/100 - Even my autocorrect is dying of laughter! Pure savagery! #RoastMyListing`);
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'facebook':
        onShare(platform);
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'linkedin':
        const linkedinText = productName
          ? `🎯 BRUTAL: Our AI just dropped a ${score}/100 on this listing! Pure savagery meets e-commerce education. This roast is what happens when AI has no chill! 🔥\n\n#EcommerceFails #AmazonFBA`
          : `🎯 When AI meets bad marketing, pure savagery ensues! This roast is a masterclass in what NOT to do on Amazon! 🔥\n\n#EcommerceFails #AmazonFBA`;
        navigator.clipboard.writeText(shareUrl);
        onShare(platform, linkedinText);
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'whatsapp':
        onShare(platform, shareText);
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}\n\n${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'copy':
        // Only copy the URL, no additional text
        navigator.clipboard.writeText(shareUrl);
        onShare(platform);
        break;
      default:
        onShare(platform);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        style={buttonStyle}
        className="viral-button viral-button-share"
        onMouseOver={(e) => {
          e.currentTarget.style.backgroundColor = '#374151';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.backgroundColor = '#4b5563';
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        <FaShareAlt size={16} />
        <span>Share ({shares})</span>
      </button>

      {isOpen && ReactDOM.createPortal(
        <div 
          ref={dropdownRef}
          className="share-dropdown-content"
          style={{
            position: 'absolute',
            top: buttonRef.current ? buttonRef.current.getBoundingClientRect().bottom + window.scrollY : 0,
            left: buttonRef.current ? buttonRef.current.getBoundingClientRect().left + window.scrollX : 0,
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
            padding: '12px',
            minWidth: '220px',
            zIndex: 9999999,
            display: 'flex',
            flexDirection: 'column',
            gap: '8px'
          }}
        >
          <button 
            onClick={() => handleShare('twitter')} 
            style={dropdownButtonStyle}
          >
            <FaTwitter size={16} />
            <span>Twitter</span>
          </button>
          <button 
            onClick={() => handleShare('facebook')} 
            style={dropdownButtonStyle}
          >
            <FaFacebook size={16} />
            <span>Facebook</span>
          </button>
          <button 
            onClick={() => handleShare('linkedin')} 
            style={dropdownButtonStyle}
          >
            <FaLinkedin size={16} />
            <span>LinkedIn</span>
          </button>
          <button 
            onClick={() => handleShare('whatsapp')} 
            style={dropdownButtonStyle}
          >
            <FaWhatsapp size={16} />
            <span>WhatsApp</span>
          </button>
          <button 
            onClick={() => handleShare('copy')} 
            style={dropdownButtonStyle}
          >
            <FaLink size={16} />
            <span>{copySuccess ? 'Link Copied!' : 'Copy Link'}</span>
          </button>
        </div>,
        document.body
      )}

      {notification && (
        <div 
          style={{
            position: 'fixed',
            top: '10px',
            right: '10px',
            backgroundColor: '#3b82f6',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            zIndex: 9999999,
            animation: 'fadeIn 0.3s ease-out forwards',
            maxWidth: '90%',
            textAlign: 'center'
          }}
        >
          <span role="img" aria-label="info">ℹ️</span> {notification}
        </div>
      )}

      <style>{`
        .share-dropdown-content {
          animation: fadeIn 0.2s ease-out;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};
